<template>
  <v-dialog v-model="dialog" persistent max-width="900px">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        class="lead-bottom-control-panel__btn"
        color="green darken-1"
        v-bind="attrs"
        v-on="on"
        :disabled="blocked"
      >
        <v-icon color="white">mdi-pencil</v-icon>
        <span class="lead-bottom-control-panel__item-text white--text">{{ btnText }}</span>
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        <span class="headline">Client information</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-form ref="formBase" v-model="valid" :lazy-validation="lazy">
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="lead.fullname"
                  :rules="emptyRules"
                  label="Full Name"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  validate-on-blur
                  v-model="lead.email"
                  label="Email"
                  :rules="emailRules"
                  type="email"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  validate-on-blur
                  counter="13"
                  v-model="lead.phone"
                  v-mask="'(###)###-####'"
                  :rules="phoneValidate"
                  label="Phone"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  validate-on-blur
                  counter="13"
                  v-model="lead.second_phone"
                  v-mask="'(###)###-####'"
                  :rules="phoneValidate"
                  label="Second Phone"
                />
              </v-col>
              <v-col cols="12" sm="6" md="5">
                <v-text-field
                  validate-on-blur
                  v-model="lead.address"
                  label="Address"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  validate-on-blur
                  counter="5"
                  v-mask="'#####'"
                  v-model="lead.zipcode"
                  label="Zip code"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-autocomplete
                  :rules="emptyRules"
                  v-model="lead.state"
                  :items="baseData.states"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  persistent-hint
                  label="State"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  :rules="emptyRules"
                  v-model="lead.birth_month"
                  :items="baseData.months"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Month"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  :rules="emptyRules"
                  v-mask="'##'"
                  v-model="lead.birth_day"
                  label="DD"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  :rules="emptyRules"
                  v-mask="'####'"
                  v-model="lead.birth_year"
                  label="YYYY"
                  required
                />
              </v-col>
              <v-row class="mx-0" align="center">
                <v-col cols="12" sm="6" md="2">
                  <v-select
                    v-model="lead.height"
                    :items="baseData.heights"
                    item-text="text"
                    item-value="value"
                    menu-props="auto"
                    label="Height"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-text-field
                    v-mask="'###'"
                    v-model="lead.weight"
                    label="Pounds"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="5">
                  <v-select
                    v-model="lead.medications"
                    :items="baseData.medications"
                    item-text="name"
                    item-value="code"
                    label="Disease"
                    multiple
                  >
                    <template v-slot:selection="{ index }">
                      <v-chip v-if="index === 0">
                        <span>{{
                          baseData.medications[lead.medications[index] - 1].name
                        }}</span>
                      </v-chip>
                      <span v-if="index === 1" class="grey--text caption"
                        >(+{{ lead.medications.length - 1 }} others)</span
                      >
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="quote-details">
                    <p class="toggle-text">Gender</p>
                    <div class="button r button-gender">
                      <input
                        v-model="lead.gender"
                        true-value="f"
                        false-value="m"
                        type="checkbox"
                        class="checkbox"
                        required
                      />
                      <div class="knobs"></div>
                      <div class="layer gender-layer"></div>
                    </div>
                  </div>
                </v-col>
              </v-row>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details">
                  <p class="toggle-text">Married</p>
                  <div class="button r button-1">
                    <input
                      v-model="lead.married"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details">
                  <p class="toggle-text">Children</p>
                  <div class="button r button-1">
                    <input
                      v-model="lead.children"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="lead.howchildren"
                  :items="baseData.childrens"
                  :disabled="!+lead.children"
                  menu-props="auto"
                  label="Amount children"
                />
              </v-col>
              <v-col cols="3"></v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details">
                  <p class="toggle-text">Tobacco</p>
                  <div class="button r button-1">
                    <input
                      v-model="lead.tobacco"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                      required
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details">
                  <p class="toggle-text">Currently insured</p>
                  <div class="button r button-1">
                    <input
                      v-model="lead.insurance"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details">
                  <p class="toggle-text">Chargeback</p>
                  <div class="button r button-1">
                    <input
                        v-model="lead.chargeback"
                        :true-value="1"
                        :false-value="0"
                        type="checkbox"
                        class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="3"></v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                    validate-on-blur
                    v-model="lead.hobby"
                    label="Favorite hobby"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                    validate-on-blur
                    v-model="lead.color"
                    label="Favorite color"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3" v-if="lead.post_sale">
                <v-text-field
                    validate-on-blur
                    v-model="lead.policy_number"
                    label="Policy number"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                    v-mask="'##/##/####'"
                    placeholder="mm/dd/yyyy"
                    validate-on-blur
                    v-model="lead.draft_date"
                    label="Draft date"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                    v-mask="'##/##/####'"
                    placeholder="mm/dd/yyyy"
                    validate-on-blur
                    v-model="lead.app_date"
                    label="Submit Date"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  :rules="emptyRules"
                  v-model="lead.term"
                  :items="baseData.terms"
                  @change="changeProductType"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Product Type"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-if="lead.term == 'fex'"
                  :rules="emptyRules"
                  v-model="lead.rate_class"
                  :items="baseData.fexRateClass"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Rate Class"
                  required
                />
                <v-select
                  v-else
                  :rules="emptyRules"
                  v-model="lead.rate_class"
                  :items="baseData.rateClass"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Rate Class"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-if="lead.term == 'fex'"
                  :rules="emptyRules"
                  v-model="lead.coverage_amount"
                  :items="baseData.covarageAmountsFEX"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Covarage Amount"
                  required
                />

                <v-select
                  v-else
                  :rules="emptyRules"
                  v-model="lead.coverage_amount"
                  :items="baseData.covarageAmounts"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Covarage Amount"
                  required
                />
              </v-col>
              <v-col v-if="lead.term === 'fex'" cols="12" sm="6" md="3">
                <v-select
                  v-model="lead.disability"
                  :rules="emptyRules"
                  :disabled="!+lead.policy && lead.term !== 'fex'"
                  :items="baseData.disabilities"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Funeral type"
                />
              </v-col>
              <v-col cols="3"></v-col>
              <v-col v-if="lead.term !== 'fex'" cols="12" sm="6" md="3">
                <v-select
                  :rules="emptyRules"
                  v-model="lead.jobstatus"
                  :items="baseData.jobStatus"
                  @change="jobstatusHandler"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Job status"
                />
              </v-col>
              <v-col cols="9"></v-col>
              <v-col
                v-if="lead.jobstatus === '1' && lead.term !== 'fex'"
                cols="12"
                sm="6"
                md="3"
              >
                <v-text-field
                  v-mask="'$##################'"
                  :rules="emptyRules"
                  v-model="lead.total_income"
                  label="Income per year"
                />
              </v-col>
              <v-row
                class="mx-0"
                v-else-if="
                  (lead.jobstatus === '2' || lead.jobstatus === '4') &&
                  lead.term !== 'fex'
                "
              >
                <v-col cols="12" sm="6" md="3">
                  <div class="quote-details">
                    <p class="toggle-text">Mortgage</p>
                    <div class="button r button-1">
                      <input
                        v-model="lead.mortgage"
                        :true-value="1"
                        :false-value="0"
                        type="checkbox"
                        class="checkbox"
                      />
                      <div class="knobs"></div>
                      <div class="layer"></div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field
                    v-model="lead.mortgage_remaining"
                    :disabled="!+lead.mortgage"
                    label="Remainning Mortgage"
                    v-mask="'$##################'"
                  />
                </v-col>
              </v-row>
              <v-row
                class="mx-0"
                v-else-if="lead.jobstatus === '3' && lead.term !== 'fex'"
              >
                <v-col cols="12" sm="6" md="3">
                  <div class="quote-details">
                    <p class="toggle-text">Final expense</p>
                    <div class="button r button-1">
                      <input
                        v-model="lead.policy"
                        :true-value="1"
                        :false-value="0"
                        type="checkbox"
                        class="checkbox"
                      />
                      <div class="knobs"></div>
                      <div class="layer"></div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-select
                    v-model="lead.disability"
                    :disabled="!+lead.policy"
                    :items="baseData.disabilities"
                    item-text="name"
                    item-value="code"
                    menu-props="auto"
                    label="Funeral type"
                  />
                </v-col>
              </v-row>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-col cols="3">
          <div class="quote-details quote-details_std">
            <p class="toggle-text">BANK ACCOUNT</p>
            <div class="button r button-1">
              <input
                v-model="lead.checking_account"
                :true-value="1"
                :false-value="0"
                type="checkbox"
                class="checkbox"
                required
              />
              <div class="knobs"></div>
              <div class="layer"></div>
            </div>
          </div>
        </v-col>
        <v-btn color="blue darken-1" text @click="dialog = false">Close</v-btn>
        <template v-if="methods === 'update'">
          <v-btn
            :disabled="formIsValid"
            color="blue darken-1"
            text
            @click="update"
            >Update</v-btn
          >
        </template>
        <template v-else-if="methods === 'create'">
          <v-btn
            :disabled="formIsValid"
            color="blue darken-1"
            text
            @click="createNewLead"
            >Create</v-btn
          >
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import FormatService from "@/services/format.service.js";

export default {
  name: "LeadBaseDataForm",
  props: {
    lead: Object,
    baseData: Object,
    btnText: String,
    methods: String,
    type: String,
    blocked: Boolean,
  },
  data() {
    return {
      dialog: false,
      lazy: false,
      valid: false,
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) =>
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i.test(
            v
          ) || "E-mail must be valid",
      ],
      emptyRules: [(v) => !!v || "This field is required"],
      phoneValidate: [
        (v) => !!v || "Phone is required",
        (v) =>
          v.toString().length === 13 ||
          "Please enter a valid 13-digit phone number",
      ],
    };
  },
  methods: {
    changeProductType() {
      if (this.lead.term == "fex") {
        this.lead.rate_class = "lb";
        this.lead.policy = 1;
        this.lead.mortgage_remaining = null;
        this.lead.mortgage = 0;
        this.lead.jobstatus = null;
      } else {
        this.lead.rate_class = "s";
        this.lead.policy = 0;
        this.lead.disability = null;
      }
    },
    jobstatusHandler() {
      this.lead.policy = 0;
      this.lead.disability = null;
      this.lead.mortgage_remaining = null;
      this.lead.mortgage = 0;
      this.lead.total_income = null;
    },
    update() {
      this.dialog = false;
      let newCopyLeads = JSON.parse(JSON.stringify(this.lead));

      newCopyLeads.empty = 0;
      newCopyLeads.birth_date = `${newCopyLeads.birth_year}-${newCopyLeads.birth_month}-${newCopyLeads.birth_day}`;
      newCopyLeads.type = "life";

      if (this.user.role_id == 2) {
        newCopyLeads.agent = this.user.id;
      }

      newCopyLeads.gender = newCopyLeads.gender ? newCopyLeads.gender : "m";

      const formatedLead = FormatService.format(newCopyLeads);

      this.$socket.emit("process-lead", formatedLead);
    },
    createNewLead() {
      this.dialog = false;

      let newCopyLeads = JSON.parse(JSON.stringify(this.lead));

      newCopyLeads.empty = 0;
      newCopyLeads.birth_date = `${newCopyLeads.birth_year}-${newCopyLeads.birth_month}-${newCopyLeads.birth_day}`;
      newCopyLeads.source = "manual";
      newCopyLeads.type = "life";

      if (this.user.role_id == 2) {
        newCopyLeads.agent = this.user.id;
      }

      newCopyLeads.gender = newCopyLeads.gender ? newCopyLeads.gender : "m";

      const formatedLead = FormatService.format(newCopyLeads);
      this.$socket.emit("process-lead", formatedLead);
    },
    emptyLead() {
      this.lead.insurance = 0;
      this.lead.gender = "m";
      this.lead.tobacco = 0;
      this.lead.zipcode = "";
      this.lead.birth_year = "";
      this.lead.birth_month = null;
      this.lead.birth_day = "";
      this.lead.height = null;
      this.lead.weight = "";
      this.lead.coverage_amount = null;
      this.lead.married = 0;
      this.lead.children = 0;
      this.lead.howchildren = null;
      this.lead.total_income = null;
      this.lead.mortgage = 0;
      this.lead.mortgage_remaining = null;
      this.lead.state = null;
      this.lead.ter = null;
      this.lead.rateClass = null;
      this.lead.jobstatus = null;
      this.lead.disability = null;
      this.lead.medications = null;
      this.lead.fullname = "";
      this.lead.phone = "";
      this.lead.email = "";
      this.lead.hobby = "";
      this.lead.color = "";
      this.lead.post_sale = 0;
      this.lead.policy_number = "";
    },
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    formIsValid: function () {
      return !this.valid;
    },
  },
};
</script>
